import { MapPin } from "lucide-react";
import type { UserAPI } from "../../../shared/types/api";
import { useAllQuizQuestions } from "../data/quiz";
import { ProfileImage } from "./ProfileImage";
import { Pill } from "./ux/Pill";

/**
 * A user's public appearance — avatar, name · age, pronouns, and city. Quiz
 * answers are intentionally not shown. Pure presentation; fetch the data with
 * `useUserPublicProfile`.
 */
export function PublicProfile({ profile }: { profile: UserAPI.PublicProfile }) {
  const allQuestions = useAllQuizQuestions();
  const pronounsQ = allQuestions.find((q) => q.id === "pronouns");
  const pronounLabels = profile.pronouns.map(
    (v) => pronounsQ?.options?.find((o) => o.value === v)?.label ?? v.replace(/_/g, "/"),
  );

  const displayName = profile.lastInitial
    ? `${profile.firstName} ${profile.lastInitial}.`
    : profile.firstName;

  return (
    <div className="flex flex-col items-center text-center">
      <ProfileImage
        size="xl"
        user={{
          firstName: profile.firstName,
          lastName: profile.lastInitial,
          profileImageUrl: profile.profileImageUrl,
        }}
      />
      <h2 className="mt-3 text-2xl font-bold dark:text-neutral-100">
        {displayName}
        {profile.age != null && (
          <span className="text-neutral-500 dark:text-neutral-400 font-semibold"> · {profile.age}</span>
        )}
      </h2>
      <div className="mt-1.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
        {pronounLabels.length > 0 && (
          <span className="text-sm text-neutral-500 dark:text-neutral-400">
            {pronounLabels.join(" / ")}
          </span>
        )}
        {profile.cityName && (
          <span className="flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400">
            <MapPin className="w-4 h-4 text-primary-500" />
            {profile.cityName}
          </span>
        )}
      </div>

      {profile.bio && (
        <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-neutral-700 dark:text-neutral-200">
          {profile.bio}
        </p>
      )}

      {profile.interestTags && profile.interestTags.length > 0 && (
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {profile.interestTags.map((t) => (
            <Pill key={t} variant="soft" size="sm">
              {t}
            </Pill>
          ))}
        </div>
      )}

      {profile.currentlyConsuming?.length > 0 && (
        <PublicTagGroup label="Currently into" tags={profile.currentlyConsuming} />
      )}
      {profile.lookingTo?.length > 0 && (
        <PublicTagGroup label="Looking to" tags={profile.lookingTo} />
      )}
    </div>
  );
}

/** A small labelled pill cloud used for the public expressive prompts. */
function PublicTagGroup({ label, tags }: { label: string; tags: string[] }) {
  return (
    <div className="mt-4">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500 mb-2">
        {label}
      </p>
      <div className="flex flex-wrap justify-center gap-2">
        {tags.map((t) => (
          <Pill key={t} variant="soft" size="sm">
            {t}
          </Pill>
        ))}
      </div>
    </div>
  );
}
